import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface SelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

interface SelectTriggerProps {
  children: React.ReactNode;
  className?: string;
  id?: string;
}

interface SelectValueProps {
  placeholder?: string;
}

interface SelectContentProps {
  children: React.ReactNode;
}

interface SelectItemProps extends React.ComponentPropsWithoutRef<"div"> {
  value: string;
  children: React.ReactNode;
}

const SelectContext = React.createContext<{
  value?: string;
  onValueChange?: (value: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedLabel?: string;
  setSelectedLabel: (label: string) => void;
}>({
  open: false,
  setOpen: () => {},
  setSelectedLabel: () => {},
});

export function Select({ value, onValueChange, children, className }: SelectProps) {
  const [open, setOpen] = React.useState(false);
  const [selectedLabel, setSelectedLabel] = React.useState<string | undefined>();

  // Find selected label from children by traversing the tree
  React.useEffect(() => {
    const findLabel = (node: React.ReactNode): string | undefined => {
      if (React.isValidElement(node)) {
        if (node.type === SelectItem && node.props.value === value) {
          return typeof node.props.children === 'string' 
            ? node.props.children 
            : undefined;
        }
        if (node.props?.children) {
          return findLabel(node.props.children);
        }
      }
      if (Array.isArray(node)) {
        for (const child of node) {
          const found = findLabel(child);
          if (found) return found;
        }
      }
      return undefined;
    };

    const label = findLabel(children);
    setSelectedLabel(label);
  }, [value, children]);

  return (
    <SelectContext.Provider value={{ value, onValueChange, open, setOpen, selectedLabel, setSelectedLabel }}>
      <div className={cn("relative", className)}>
        {children}
      </div>
    </SelectContext.Provider>
  );
}

export function SelectTrigger({ children, className, id }: SelectTriggerProps) {
  const { open, setOpen } = React.useContext(SelectContext);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        !target.closest('[data-select-content]')
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open, setOpen]);

  return (
    <button
      ref={triggerRef}
      id={id}
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(!open);
      }}
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 ring-offset-background placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
    >
      {children}
      <ChevronDown className={cn("h-4 w-4 opacity-50 transition-transform", open && "rotate-180")} />
    </button>
  );
}

export function SelectValue({ placeholder }: SelectValueProps) {
  const { value, selectedLabel } = React.useContext(SelectContext);

  return (
    <span className="block truncate text-left">
      {selectedLabel || value || placeholder || "Select..."}
    </span>
  );
}

export function SelectContent({ children }: SelectContentProps) {
  const { onValueChange, open, setOpen } = React.useContext(SelectContext);

  if (!open) return null;

  return (
    <div
      data-select-content
      className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === SelectItem) {
          return React.cloneElement(child, {
            onClick: (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              onValueChange?.(child.props.value);
              setOpen(false);
            }
          } as any);
        }
        return child;
      })}
    </div>
  );
}

export function SelectItem({ value, children, className, onClick, onKeyDown, ...props }: SelectItemProps) {
  const { value: selectedValue } = React.useContext(SelectContext);
  const isSelected = selectedValue === value;

  return (
    <div
      role="option"
      tabIndex={0}
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors",
        isSelected
          ? "bg-primary/10 text-primary font-medium"
          : "hover:bg-gray-100 text-gray-900 focus:bg-gray-100",
        className
      )}
      onClick={(event) => {
        onClick?.(event);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.(event as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>);
        }
        onKeyDown?.(event);
      }}
      {...props}
    >
      {children}
      {isSelected && (
        <span className="absolute left-2 flex items-center">
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </span>
      )}
    </div>
  );
}